/* eslint-disable react-i18n/no-dynamic-translation-keys */
import React, { useEffect, useState } from "react";
import moment from "moment";
import ReactTooltip from "react-tooltip";
import { CustomerSucessController } from "@talentbait/shared-components";

import * as SC from "./CampaignCreateBaseData.styled";

import Actions from "../../../../Actions";
import {
  CampaignCreationStore,
  PreviewStore,
  MediaLibraryStore,
  withRefluxStore
} from "../../../../Stores";

import CampaignCreateLocationSelector from "./../../LocationSelector/Campaign__Create_LocationSelector";
import Input from "../../../../Components/Input";
import { Heading } from "../../../../Components/Heading";

import { withHttp, isValidHttpUrl } from "./../../Previews/utils";
import logger from "../../../../utils/LoggerFrontend";
import {
  getBudgetByDuration,
  cleanTitle,
  calculateExtraLocationBudget
} from "../../../../utils/Helpers";
import { getCompanyImageFolderName } from "../../../../utils/GetCompanyFolderName";
import { useTranslation } from "react-i18next";
import { getLanguageByLocale } from "../../../ad-assistant/utils";
import { SelectInput } from "../../../../Components/Form";

const CAMPAIGN_DURATION_OPTIONS = /** @type {const} */ ([
  { label: "{{duration}} days", value: 30, displayDuration: 30 },
  { label: "{{duration}} days", value: 60, displayDuration: 60 },
  { label: "{{duration}} days", value: 90, displayDuration: 90 },
  { label: "{{duration}} months", value: 180, displayDuration: 6 },
  { label: "{{duration}} months", value: 365, displayDuration: 12 },
  { label: "{{duration}} months", value: 730, displayDuration: 24 },
  { label: "{{duration}} days", value: 19, displayDuration: 19 },
  { label: "{{duration}} weeks", value: 42, displayDuration: 6 },
  { label: "Until the end of the year", value: 365 - moment().dayOfYear() }
]);

const CAMPAIGN_LOCALE_OPTIONS = /** @type {const} */ ([
  { label: "English", value: "en_US" },
  { label: "German", value: "de_DE" },
  { label: "Spanish", value: "es_ES" }
]);

const CampaignCreateBaseData = ({
  isBudgetCheck,
  userId,
  userName,
  // Store
  activeDate,
  startDate,
  companyName,
  companyId,
  jobTitle,
  jobPostingUrl,
  location,
  organization,
  campaignDuration,
  lifeTimeBudget,
  atsId,
  draftCampaignId,
  selectedChannels,
  startCampaignCreation,
  locale
}) => {
  const { t } = useTranslation();
  const [needBudgetCheck, setNeedBudgetCheck] = useState(false);
  const [errors, setErrors] = useState({
    hasErrorCompanyName: false,
    hasErrorJobTitle: false,
    hasErrorJobPostingUrl: false,
    hasErrorLocation: false
  });
  const [requestedJobCategory, setRequestedJobCategory] = useState("");

  useEffect(() => {
    logger.log("BaseData:componentDidMount", "called", {
      companyName,
      companyId,
      jobTitle,
      jobPostingUrl,
      location,
      organization,
      campaignDuration,
      locale
    });
    CustomerSucessController.send("CreateCampaign:baseData:started");

    setNeedBudgetCheck(false);
  }, []);

  const handleInputs = (input, value) => {
    setNeedBudgetCheck(true);

    Actions.campaignCreateUpdateBaseData({ [input]: value });
  };

  const handleChangeDuration = ({ value }) => {
    logger.debug("CampaignCreate:handleChangeDuration", "called with", {
      lifeTimeBudget,
      campaignDuration
    });

    const newLifetimeBudget = getBudgetByDuration(lifeTimeBudget, value, campaignDuration);

    handleInputs("campaignDuration", value);
    handleInputs("lifeTimeBudget", newLifetimeBudget);
  };

  const checkError = () => {
    let isOverlapping = false;

    location.forEach((item, locIndex) => {
      if (item.type === "city") {
        for (let i = 0; i < locIndex; i++) {
          if (item.stateOfLoc === location[i].location) {
            isOverlapping = true;
          }
        }
        for (let i = location.length - 1; i > locIndex; i--) {
          if (item.stateOfLoc === location[i].location) {
            isOverlapping = true;
          }
        }
      }
    });

    logger.debug("CampaignCreateBaseData:checkError", "called");
    let hasError = "";

    if (!isBudgetCheck && companyName.length < 1) {
      setErrors({ ...errors, hasErrorCompanyName: true });
      hasError = "Please check the company name";
    } else if (jobTitle.length < 1) {
      setErrors({ ...errors, hasErrorJobTitle: true });
      hasError = "Please check the job title";
    } else if (jobPostingUrl.length < 1) {
      setErrors({ ...errors, hasErrorJobPostingUrl: true });
      hasError = "Please check the link";
    } else if (!isValidHttpUrl(jobPostingUrl)) {
      setErrors({ ...errors, hasErrorJobPostingUrl: true });
      hasError = "Please check the link";
    } else if (location.length < 1) {
      setErrors({ ...errors, hasErrorLocation: true });
      hasError = "Please check the location";
    } else if (isOverlapping) {
      setErrors({ ...errors, hasErrorLocation: true });
      hasError = "Please check the locations: there are overlapping locations";
    }

    return hasError;
  };

  useEffect(() => {
    if (startCampaignCreation) {
      handleOnNextClick();

      Actions.startCampaignCreation(false);
    }
  }, [startCampaignCreation]);

  const handleOnNextClick = () => {
    logger.log("CampaignCreateBaseData:handleOnNextClick", "user wants to go to next page");
    const hasError = checkError();

    if (hasError) {
      Actions.addNotification(t(hasError), "error");
      logger.log("CampaignCreateBaseData:handleOnNextClick", "found error", { error: hasError });
      return;
    }

    const orgaName = organization && organization.name;
    const organizationId = organization && organization.id;

    const cloudinaryData = { jobTitle: cleanTitle(jobTitle) };
    const { folderName, metadata } = getCompanyImageFolderName({
      orgaId: organizationId,
      companyId,
      orgaName,
      companyName,
      metadata: cloudinaryData
    });
    const language = getLanguageByLocale(locale || "de_DE");

    const data = {
      jobPostingUrl,
      jobTitle,
      orgaName,
      orgaId: organizationId,
      companyId,
      companyName,
      location,
      folderName,
      metadata,
      atsId,
      draftCampaignId,
      userId,
      userName,
      locale,
      language
    };

    CustomerSucessController.send("CreateCampaign:baseData:finished", data);

    if (!isBudgetCheck) {
      Actions.getAdPreviewData(data);
      Actions.getTeaserTextRecommendation({
        url: jobPostingUrl,
        jobTitle,
        companyName,
        location: location?.[0],
        orgaId: organization && organization.id
      });

      if (organization.campaignGoal === "applicants" && !data.campaignId) {
        Actions.generateJobPage(data);
      }
    }

    logger.log("CampaignCreateBaseData:handleOnNextClick", "fetching preview data", {
      ...data,
      time: moment().format()
    });

    const isFirstTime = selectedChannels.length === 0;

    Actions.campaignCreateUpdateBaseData(
      {
        currentStep: "STEP_PRICE_PACKAGE"
      },
      isFirstTime ? true : needBudgetCheck
    );
  };

  const onBlur = () => {
    const merged = (jobTitle + jobPostingUrl).toLowerCase();

    if (isValidHttpUrl(jobPostingUrl) && jobTitle && requestedJobCategory !== merged) {
      Actions.getJobCategory({ jobPostingUrl, jobTitle });
      setRequestedJobCategory(merged);
    }

    if (!companyId) {
      Actions.getOrCreateCompany({ name: companyName, organizationId: organization.id });
    }
  };

  const onBlurCompany = () => {
    Actions.getOrCreateCompany({ name: companyName, organizationId: organization.id });
  };

  let durationOptionsToShow = [...CAMPAIGN_DURATION_OPTIONS];

  if (organization?.signupType == "PLG") {
    durationOptionsToShow.unshift({ label: "{{duration}} days", value: 14, displayDuration: 14 });
  }

  durationOptionsToShow = durationOptionsToShow.map(({ label, value, displayDuration }) => ({
    label: t(label, { duration: displayDuration }),
    value
  }));

  logger.debug("CampaignCreateBaseData", "render", {
    windowLanguage: window?.language
  });

  return (
    <div>
      <div className="App center fade-in">
        <div className="create__company-heading">
          <Heading
            title={isBudgetCheck ? t("Perform budget check") : t("Create a new campaign")}
            subtitle={
              isBudgetCheck
                ? t("Ask the system for a budget and channel recommendation now")
                : t("Please fill in the necessary data to create the campaign.")
            }
          />
          <div className="create__company-step">
            {t("Step")} 1 / {isBudgetCheck ? "2" : "4"}
          </div>
        </div>

        <div className="create__company-wrapper">
          {!isBudgetCheck && (
            <Input
              showError={errors.hasErrorCompanyName}
              placeholder={t("Company name")}
              onChange={(value) => {
                setErrors({ ...errors, hasErrorCompanyName: value.length < 1 });
                handleInputs("companyName", value);
              }}
              style={{ margin: "0 auto", marginTop: 15 }}
              exampleText={t("e.g. Mustermann GmbH")}
              value={companyName}
              mandatory={true}
              onBlur={onBlurCompany}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
          )}
          <Input
            showError={errors.hasErrorJobTitle}
            placeholder={t("Job title")}
            onChange={(value) => {
              setErrors({ ...errors, hasErrorJobTitle: value.length < 1 });
              handleInputs("jobTitle", value);
            }}
            style={{ margin: "0 auto", marginTop: 15 }}
            exampleText={t("e.g. Marketing Manager")}
            value={jobTitle}
            mandatory={true}
            onBlur={onBlur}
          />
          <Input
            showError={errors.hasErrorJobPostingUrl}
            placeholder={t("URL to open position")}
            onChange={(value) => {
              setErrors({ ...errors, hasErrorJobPostingUrl: value.length < 1 });
              handleInputs("jobPostingUrl", withHttp(value));
            }}
            style={{ margin: "0 auto", marginTop: 15 }}
            exampleText={t("example_jobpost_url")}
            value={jobPostingUrl}
            mandatory={true}
            onBlur={onBlur}
          />

          <CampaignCreateLocationSelector
            showError={errors.hasErrorLocation}
            location={location}
            orgaId={organization.id}
            onChangeRemote={(value) => {
              handleInputs("isRemoteJob", value);
            }}
            onSelect={(location) => {
              const extraBudgetLocations = calculateExtraLocationBudget(location);
              handleInputs("location", location);
              handleInputs("extraBudgetLocations", extraBudgetLocations);
            }}
          />
          <br />

          <SC.CampaignDurationWrapper>
            <SC.CampaignDate>
              <SC.WrapHeadline>{t("Start date")}</SC.WrapHeadline>
              <SC.OptionsWrapper>
                <SC.InputWrapper activate={activeDate === "now"}>
                  <SC.GeneralInput
                    type="radio"
                    id="date"
                    onChange={(e) => handleInputs("activeDate", e.target.value)}
                    checked={activeDate === "now"}
                    value="now"
                  />
                  <label htmlFor="date">{t("As of now")}</label>
                </SC.InputWrapper>
                <SC.InputWrapper activate={activeDate === "later"}>
                  <SC.GeneralInput
                    type="radio"
                    id="date2"
                    onChange={(e) => handleInputs("activeDate", e.target.value)}
                    checked={activeDate === "later"}
                    value="later"
                  />
                  <label htmlFor="date2">{t("Later")}</label>
                  <SC.InputDate
                    type="date"
                    onChange={(e) => handleInputs("startDate", e.target.value)}
                    value={startDate || ""}
                    hidden={activeDate !== "later"}
                  />
                </SC.InputWrapper>
              </SC.OptionsWrapper>
            </SC.CampaignDate>

            <SC.CampaignDuration>
              <SC.WrapHeadline>{t("Duration")}</SC.WrapHeadline>
              <SC.InputWrapper activate={activeDate === "later"}>
                <SC.SelectContainer>
                  <SelectInput
                    id="campaign-duration"
                    placeholder={t("Select duration")}
                    options={durationOptionsToShow}
                    value={durationOptionsToShow.find((o) => o.value === campaignDuration)}
                    onChange={handleChangeDuration}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    styles={SC.customStyles}
                  />
                </SC.SelectContainer>
              </SC.InputWrapper>
            </SC.CampaignDuration>

            <SC.CampaignLang>
              <SC.WrapHeadline>{t("Target language")}</SC.WrapHeadline>
              <SC.InputWrapper>
                <SC.SelectContainer>
                  <SelectInput
                    id="campaign-locale"
                    placeholder={t("Select language")}
                    options={CAMPAIGN_LOCALE_OPTIONS}
                    value={CAMPAIGN_LOCALE_OPTIONS.find((o) => o.value === locale)}
                    onChange={({ value }) => handleInputs("locale", value)}
                    menuPlacement="auto"
                    styles={SC.customStyles}
                  />
                </SC.SelectContainer>
              </SC.InputWrapper>
            </SC.CampaignLang>
          </SC.CampaignDurationWrapper>

          <div className="wrap__collapse" />

          <ReactTooltip className="campaign-read__tooltip" />
        </div>
      </div>
    </div>
  );
};

export default withRefluxStore(
  CampaignCreateBaseData,
  [CampaignCreationStore, PreviewStore, MediaLibraryStore],
  [
    "activeDate",
    "startDate",
    "companyName",
    "companyId",
    "jobTitle",
    "jobPostingUrl",
    "location",
    "organization",
    "campaignDuration",
    "lifeTimeBudget",
    "atsId",
    "draftCampaignId",
    "selectedChannels",
    "startCampaignCreation",
    "locale"
  ]
);
